/**
 * @fileoverview Drag event handlers for toddler game.
 * Manages dragstart, dragmove, dragend events and success/failure logic.
 */

import Konva from 'konva';
import { CONFIG } from './GameConfig.js';
import { ColorUtils } from '../utilities/ColorUtils.js';
import { ToddlerGame } from './ToddlerGame.js';
import { GameUtils, MatchingPair } from '../utilities/GameUtils.js';
import { CustomNode, CustomShape, CustomGroup, KonvaEventAbstract, KonvaEventReal } from '../konva-extensions.js';
import { SparkleTrail } from '../utilities/SparkleTrail.js';
import { AnimationService } from '../services/AnimationService.js';

/**
 * Drag event handlers for the toddler game.
 * Handles all drag interactions, validation, and game logic for matching.
 */
export class DragHandlers {
    game: ToddlerGame;
    sparkleTrail: SparkleTrail | null = null;
    animationService: AnimationService;
    
    /**
     * @param {ToddlerGame} game - ToddlerGame instance
     */
    constructor(game: ToddlerGame) {
        this.game = game;
        this.animationService = new AnimationService();
    }
    
    /**
     * Initialize sparkle trail (called after layers are created)
     */
    private ensureSparkleTrail() {
        if (!this.sparkleTrail && this.game.dragLayer) {
            this.sparkleTrail = new SparkleTrail(this.game.dragLayer);
        }
    }
    
    /**
     * Show selection feedback on touch/mousedown (before drag starts).
     * @param {Konva.Node} draggable - Konva draggable shape
     * @param {Object} [evt] - Konva event object
     */
    showSelectionFeedback(draggable: CustomNode, evt?: KonvaEventAbstract) {
        // Stop movement animation immediately on touch/mousedown (only if it exists)
        if (this.game.currentTheme?.hasMovement && draggable.movementAnim) {
            this.game.movementManager?.stop(draggable);
        }
        
        // Try to unlock sound effects on first touch (for iOS when overlay is skipped)
        if (evt) {
            var nativeEvt: KonvaEventReal = evt.evt;
            this.game.audioSystem.getSoundEffects().tryUnlockFromEvent(nativeEvt);
        }
        
        // Apply selection styling - handle Groups differently
        if (draggable.getClassName && draggable.getClassName() === 'Group') {
            // For groups, apply to the first child (circle/shape)
            const group = draggable as CustomGroup;
            const children = group.getChildren();
            if (children && children.length > 0) {
                const shape = children[0] as Konva.Shape;
                shape.strokeWidth(30);
                // Shadow not supported on Group, apply to child shape
                shape.shadowEnabled(true);
                shape.shadowColor('black');
                shape.shadowBlur(10);
                shape.shadowOpacity(0.5);
            }
        } else {
            // For regular shapes, apply directly
            const shape = draggable as CustomShape;
            shape.strokeWidth(30);
            shape.shadowEnabled(true);
            shape.shadowColor('black');
            shape.shadowBlur(10);
            shape.shadowOpacity(0.5);
        }
        draggable.getLayer()?.batchDraw();
        
        // Play selection sound
        this.game.audioSystem.getSoundEffects().playSelection();
    }
    
    /**
     * Remove selection feedback.
     * @param {Object} draggable - Konva draggable shape
     */
    removeSelectionFeedback(draggable: CustomNode) {
        // Remove selection styling - handle Groups differently
        if (draggable.getClassName && draggable.getClassName() === 'Group') {
            // For groups, apply to the first child (circle/shape)
            const group = draggable as CustomGroup;
            const children = group.getChildren();
            if (children && children.length > 0) {
                const shape = children[0] as Konva.Shape;
                shape.strokeWidth(3);
                // Remove shadow from the child shape
                shape.shadowEnabled(false);
            }
        } else {
            // For regular shapes, apply directly
            const shape = draggable as CustomShape;
            shape.strokeWidth(3);
            shape.shadowEnabled(false);
        }
        draggable.getLayer()?.batchDraw();
    }
    
    /**
     * Handle drag start - scale up for visual feedback and unlock audio.
     * Moves draggable to drag layer for performance.
     * @param {Object} draggable - Konva draggable shape
     * @param {Object} [evt] - Konva event object with native event in evt.evt
     */
    onDragStart(draggable: CustomNode, evt?: KonvaEventAbstract) {
        // Don't allow dragging when game is paused
        if (this.game.isPaused) {
            return;
        }
        
        // Animation is already stopped in showSelectionFeedback, no need to stop again
        
        // Emit drag state change
        if (this.game.gameState) {
            this.game.gameState.update('isDragging', true);
        }
        
        // Move draggable and its text to drag layer for performance
        draggable.moveTo(this.game.dragLayer);
        if (draggable.numberText) {
            draggable.numberText.moveTo(this.game.dragLayer);
        }
        this.game.staticLayer!.batchDraw();
        
        // Try to unlock audio on the first real user gesture
        try {
            var nativeEvt: KonvaEventReal | undefined = evt?.evt;
            if (this.game && nativeEvt) {
                this.game.audioSystem.tryUnlockFromEvent(nativeEvt);
            }
        } catch (e) {}
        
        // Show selection feedback (may already be shown from mousedown/touchstart)
        this.showSelectionFeedback(draggable);
        
        // Ensure sparkle trail is initialized
        this.ensureSparkleTrail();
        
        // Get color for sparkles from AnimationService
        const color = this.extractColor(draggable);
        
        // Start sparkle trail
        if (this.sparkleTrail) {
            this.sparkleTrail.start(draggable.x(), draggable.y(), color);
        }
    }
    
    /**
     * Handle drag move - update text position and highlight nearby targets.
     * @param {Konva.Node} draggable - Konva draggable shape
     */
    onDragMove(draggable: CustomNode) {
        if (draggable.numberText) {
            draggable.numberText.position(draggable.position());
        }
        
        // Update sparkle trail with current position and color
        if (this.sparkleTrail) {
            const color = this.extractColor(draggable);
            this.sparkleTrail.update(draggable.x(), draggable.y(), color);
        }
        
        // Check proximity to all targets and highlight the nearby one
        // let hoveredTarget = null;
        
        for (const target of this.game.targets) {
            const distance = GameUtils.calculateDistance(draggable, target);

            // Get original color
            const pair = this.game.matchingPairs.find((p: MatchingPair) => p.tsIndex === target.tsIndex);
            const originalFill = pair ? ColorUtils.lightenColor(pair.color.fill, 0.5) : CONFIG.TARGET_FILL;
            const originalStroke = pair ? ColorUtils.lightenColor(pair.color.stroke, 0.3) : CONFIG.TARGET_STROKE;
            if (distance < CONFIG.TOLERANCE) {
                // hoveredTarget = target;
                
                // Highlight this target
                if (!target.isHighlighted) {
                    target.isHighlighted = true;
                    
                    // Check if this is the correct target
                    const isCorrect = target.tsIndex === draggable.correctTsIndex;
                    
                    // Get the color to use
                    let fillColor, strokeColor;
                    if (isCorrect) {
                        // Use the draggable's color for correct target
                        fillColor = draggable.shapeColor ? draggable.shapeColor.fill : CONFIG.TARGET_FILL;
                        strokeColor = draggable.shapeColor ? draggable.shapeColor.stroke : CONFIG.TARGET_STROKE;
                    } else {
                        fillColor = originalFill || CONFIG.TARGET_FILL;
                        strokeColor = originalStroke || CONFIG.TARGET_STROKE;
                    }
                    
                    // Animate scale and fill
                    target.to({
                        scaleX: 1.15,
                        scaleY: 1.15,
                        fill: fillColor,
                        stroke: strokeColor,
                        opacity: 0.8,
                        duration: 0.1
                    });
                }
            } else {
                // Reset target if it was highlighted
                if (target.isHighlighted) {
                    target.isHighlighted = false;
                    
                    
                    // Animate back to original state
                    target.to({
                        scaleX: 1,
                        scaleY: 1,
                        fill: originalFill,
                        stroke: originalStroke,
                        opacity: 0.6,
                        duration: 0.1
                    });
                }
            }
        }
        
        // Don't call batchDraw here - Konva's drag system handles it
    }
    
    /**
     * Handle drag end - check position and snap or return.
     * Validates drop position against correct target, wrong targets, or empty space.
     */
    onDragEnd(draggable: CustomNode) {
        // Stop sparkle trail
        if (this.sparkleTrail) {
            this.sparkleTrail.stop();
        }
        
        // Emit drag state change
        if (this.game.gameState) {
            this.game.gameState.update('isDragging', false);
        }
        
        // Reset all highlighted targets
        for (const target of this.game.targets) {
            if (target.isHighlighted) {
                target.isHighlighted = false;
                
                // Get original color
                const pair = this.game.matchingPairs.find((p: MatchingPair) => p.tsIndex === target.tsIndex);
                const originalFill = pair ? ColorUtils.lightenColor(pair.color.fill, 0.5) : CONFIG.TARGET_FILL;
                const originalStroke = pair ? ColorUtils.lightenColor(pair.color.stroke, 0.3) : CONFIG.TARGET_STROKE;
                
                // Reset to original state immediately (no animation)
                target.scaleX(1);
                target.scaleY(1);
                target.opacity(0.6);
                
                // Apply fill and stroke - only works on Shapes, not Groups
                if ('fill' in target) {
                    (target as CustomShape).fill(originalFill);
                    (target as CustomShape).stroke(originalStroke);
                }
            }
        }
        
        // Remove selection feedback
        this.removeSelectionFeedback(draggable);
        
        // Move back to static layer
        draggable.moveTo(this.game.staticLayer!);
        if (draggable.numberText) {
            draggable.numberText.moveTo(this.game.staticLayer!);
        }
        this.game.dragLayer!.batchDraw();
        this.game.staticLayer!.batchDraw();
        
        // Check distance to all targets
        const correctTarget = this.game.targets.find((t: CustomNode) => t.tsIndex === draggable.correctTsIndex);
        const distanceToCorrect = GameUtils.calculateDistance(draggable, correctTarget);
        
        // For sequential themes, also check if the target is currently visible
        const isTargetVisible = !correctTarget || correctTarget.visible();
        
        // Check if it's close to the correct target AND target is visible (for sequential themes)
        if (distanceToCorrect < CONFIG.TOLERANCE && correctTarget && isTargetVisible) {
            this.handleSuccess(draggable, correctTarget);
        } else {
            // Check if it's close to any wrong target
            const wrongTargets = this.game.targets.filter((t: CustomNode) => t.tsIndex !== draggable.correctTsIndex);
            const nearWrongTarget = wrongTargets.some((t: CustomNode) => 
                GameUtils.calculateDistance(draggable, t) < CONFIG.TOLERANCE
            );
            
            if (nearWrongTarget) {
                this.handleWrongTarget(draggable);
            } else {
                this.handleFailure(draggable);
            }
        }
    }
    
    /**
     * Handle successful drop - snap to target and play animation.
     */
    handleSuccess(draggable: CustomNode, target: CustomNode) {
        // Stop any movement animation immediately (only if it exists)
        if (this.game.currentTheme?.hasMovement && draggable.movementAnim) {
            this.game.movementManager?.stop(draggable);
        }
        
        // Mark as matched to prevent movement animation
        draggable.isMatched = true;
        
        // Disable dragging for matched shape
        draggable.draggable(false);
        
        // Play success sound
        this.game.audioSystem.getSoundEffects().playSuccess();
        
        // Capture theme reference now, before any async callbacks run,
        // to avoid the race condition where currentTheme changes to the next theme
        // before the snap animation's onFinish fires.
        const theme = this.game.currentTheme;
        
        // Snap to target
        draggable.to({
            x: target.x(),
            y: target.y(),
            duration: CONFIG.SNAP_DURATION,
            easing: Konva.Easings.EaseOut,
            onUpdate: () => {
                if (draggable.numberText) {
                    draggable.numberText.position(draggable.position());
                }
            },
            onFinish: () => {
                // log theme name:
                // Disable dragging
                draggable.draggable(false);
                
                // Mark pair as completed
                const pair = this.game.matchingPairs.find(p => p.dsIndex === draggable.dsIndex);
                if (pair) {
                    pair.completed = true;
                    GameUtils.saveProgress(this.game.matchingPairs);
                }
                
                // Play success animation
                this.animationService.playSuccessAnimation(draggable);
                
                // Show success message
                this.animationService.showSuccessMessage(this.game.feedbackText!);
                
                // Hide target
                this.animationService.fadeOutTarget(target);
                
                // Use gameState to track completion
                if (this.game.gameState) {
                    this.game.gameState.incrementCompleted();
                    // Game completion handled by state listener in game.ts
                } else {
                    // Fallback for backward compatibility
                    this.game.completedCount++;
                    if (this.game.completedCount === theme?.numPairs) {
                        setTimeout(() => this.game.resetGame(true), CONFIG.RESET_DELAY);
                    }
                }
                
                // For sequential themes, hide both draggable and target before showing next
                console.log(`✅ Matched pair in theme: ${theme?.name}`);
                if (theme?.isSequential) {
                    // Hide/destroy matched draggable and target
                    
                    // Find the current tsIndex and show the next one in sequence
                    // Find next target with tsIndex = currentTsIndex + 1
                    const currentTsIndex = target.tsIndex;
                    const nextTarget = currentTsIndex !== undefined ? this.game.targets.find((t: CustomNode) => 
                        t.tsIndex === currentTsIndex + 1 && t.isHidden && !t.isMatched
                    ) : undefined;
                    if (nextTarget) {
                        draggable.destroy();
                        if (draggable.numberText) {
                            draggable.numberText.destroy();
                        }
                        target.destroy();
                    }
                    
                    
                    if (nextTarget) {
                        nextTarget.visible(true);
                        nextTarget.isHidden = false;
                        // Fade in animation for better UX
                        nextTarget.opacity(0);
                        nextTarget.to({
                            opacity: 0.6,
                            duration: 0.3,
                            easing: Konva.Easings.EaseInOut
                        });
                    }
                } else {
                    // Non-sequential themes: only destroy target (draggable stays visible)
                    target.destroy();
                }
            }
        });
    }
    
    /**
     * Handle failed drop - return to start position.
     * @param {Konva.Node} draggable - Konva draggable shape
     */
    handleFailure(draggable: CustomNode) {
        // Play wrong sound (gentle, non-punishing)
        this.game.audioSystem.getSoundEffects().playWrong();
        
        // Animate draggable back to start
        draggable.to({
            x: draggable.originalPos!.x,
            y: draggable.originalPos!.y,
            duration: CONFIG.BOUNCE_BACK_DURATION,
            easing: Konva.Easings.EaseOut,
            onFinish: () => {
                // Resume movement animation if in moving shapes theme
                if (this.game.currentTheme?.hasMovement && !draggable.isMatched) {
                    draggable.animationStopped = false;
                    this.game.movementManager?.animateSingle(draggable);
                }
            }
        });
        
        // Animate text separately for better performance
        if (draggable.numberText && draggable.originalPos) {
            draggable.numberText.to({
                x: draggable.originalPos.x,
                y: draggable.originalPos.y,
                duration: CONFIG.BOUNCE_BACK_DURATION,
                easing: Konva.Easings.EaseOut
            });
        }
    }
    
    /**
     * Handle wrong target - DS placed near incorrect TS.
     * Returns draggable to starting position.
     * @param {Konva.Node} draggable - Konva draggable shape
     */
    handleWrongTarget(draggable: CustomNode) {
        // Play wrong sound (gentle, non-punishing)
        this.game.audioSystem.getSoundEffects().playWrong();
                // Ensure originalPos exists
        if (!draggable.originalPos) {
            console.error('originalPos not found on draggable');
            return;
        }
                // Animate draggable back to start
        draggable.to({
            x: draggable.originalPos.x,
            y: draggable.originalPos.y,
            duration: CONFIG.BOUNCE_BACK_DURATION,
            easing: Konva.Easings.EaseOut,
            onFinish: () => {
                // Resume movement animation if in moving shapes theme
                if (this.game.currentTheme?.hasMovement && !draggable.isMatched) {
                    draggable.animationStopped = false;
                    this.game.movementManager?.animateSingle(draggable);
                }
            }
        });
        
        // Animate text separately for better performance
        if (draggable.numberText && draggable.originalPos) {
            draggable.numberText.to({
                x: draggable.originalPos.x,
                y: draggable.originalPos.y,
                duration: CONFIG.BOUNCE_BACK_DURATION,
                easing: Konva.Easings.EaseOut
            });
        }
    }
    
    /**
     * Extract color from a Konva shape or group.
     * @param node - Konva shape or group
     * @returns Color hex string
     */
    private extractColor(node: any): string {
        let color = '#FFFF00'; // Default yellow color
        
        // Check if color is stored directly on the node
        if (node.shapeColor) {
            return node.shapeColor.fill || node.shapeColor.stroke || color;
        }
        
        // Try to get fill color first
        if (typeof node.fill === 'function') {
            const fillColor = node.fill();
            if (fillColor && fillColor !== '') {
                color = fillColor;
            } else if (typeof node.stroke === 'function') {
                const strokeColor = node.stroke();
                if (strokeColor && strokeColor !== '') {
                    color = strokeColor;
                }
            }
        } else if (node.children && node.children.length > 0) {
            // For Groups, get color from first child shape
            const firstChild = node.children[0];
            if (typeof firstChild.fill === 'function') {
                const fillColor = firstChild.fill();
                if (fillColor && fillColor !== '') {
                    color = fillColor;
                } else if (typeof firstChild.stroke === 'function') {
                    const strokeColor = firstChild.stroke();
                    if (strokeColor && strokeColor !== '') {
                        color = strokeColor;
                    }
                }
            }
        }
        
        return color;
    }
}
